import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import {
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl, IconButton, OutlinedInput, Popover,
  Backdrop, CircularProgress, InputLabel, InputAdornment, Menu,
  FormControlLabel, Checkbox
} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import QuestionMark from '@mui/icons-material/QuestionMark';
import { grey } from "@mui/material/colors";
import Divider from "@mui/material/Divider";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import debug from "@plannel/utils/debug-util";
import demandValService from "@plannel/services/data/demand-val-service";
import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import { addDays, addMonths, differenceInCalendarDays, differenceInMonths } from "date-fns";
import {cloneDeep, isEmpty, isEqual} from "lodash";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import dateUtils from "@plannel/utils/date-util";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import { FS } from "@plannel/utils/font-util";

const BulkValueUpdateModal = (props) => {
  const { t, close, open, nodeData, modalResultData, selectVersion } = props;

  const selectTD = nodeData?.selectTD;
  const startDay = nodeData?.params?.startDay;
  const startDayId = startDay ? dateUtils.getMyDayId(startDay) : nodeData?.startDate?.getDay();
  const { dayOfWeek, buckets  } = codeUtil;
  const isWeekBucket = codeUtil.isWeekBucket(selectTD);
  const reverseHeader = nodeData?.reverseFormatHeaders;
  const { snackBarMessageColor } = ColorUtil;
  const CURRENT_DATE = new ZDate();
  const startDayByVersion = nodeData?.selectVersion?.startDay;
  const baseMonth = new ZDate(nodeData.columnHeader);

  if (startDayId && selectTD && isWeekBucket) {
    if (CURRENT_DATE.getDay() !== startDayId) {
      const gap = startDayId - CURRENT_DATE.getDay();
      CURRENT_DATE.setDate(CURRENT_DATE.getDate() + gap);
    }
  }
  const TO_START_DATE = nodeData.editableHeaderSet ? isWeekBucket && reverseHeader ? new ZDate(reverseHeader[nodeData.editableHeaderSet[0]]) : new ZDate(nodeData.editableHeaderSet[0]) : CURRENT_DATE;
  const originData = isWeekBucket ? new ZDate(reverseHeader[nodeData?.editableHeaderSet?.slice(-1)[0]]) : new ZDate(nodeData?.editableHeaderSet?.slice(-1)[0]);
  const END_MAX_DATE = nodeData.editableHeaderSet ? originData : new ZDate(CURRENT_DATE.getFullYear() + 1, CURRENT_DATE.getMonth(), CURRENT_DATE.getDate(), 0, 0, 0, 0);
  isWeekBucket ? END_MAX_DATE.setDate(END_MAX_DATE.getDate() + 7) : END_MAX_DATE.setDate(END_MAX_DATE.getDate() + 1);
  const FROM_START_DATE =  nodeData?.startDate;
  
  const periodList = [t("custom"), t("lastMonth"), t("last6months"), t("lastYear")];
  const [isPeriod, setIsPeriod] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(periodList[0]);

  const [copyFromStartDate, setCopyFromStartDate] = useState(CURRENT_DATE);
  const [copyToStartDate, setCopyToStartDate] = useState(CURRENT_DATE);
  const [copyFromEndDate, setCopyFromEndDate] = useState(CURRENT_DATE);
  const [copyToEndDate, setCopyToEndDate] = useState(CURRENT_DATE);

  const [expression, setExpression] = useState("");
  const [enabledMeasure, setEnabledMeasure] = useState([]);

  const [sourceMeasure, setSourceMeasure] = useState("");
  const [isIncorrect, setIsIncorrect] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const [isPage, setIsPage] = useState(false);
  const [isVersion, setIsVersion] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [toggleOpen, setToggleOpen] = useState(false);

  const popoverOpen = Boolean(anchorEl);
  const id = popoverOpen ? 'simple-popover' : null;
  const menuOpen = Boolean(menuEl);
  const versionType = codeUtil.dpVersion

  const popoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const popoverClose = () => {
    setAnchorEl(null);
  };

  const menuKeyPress = (event) => {
    if (isEqual(event.key, "[")) {
      setMenuEl(event.currentTarget);
    }
  };

  const menuClose = () => {
    setMenuEl(null);
  };

  const addExpression = (e) => {
    if (expression.match(/\]/) === null) {
      menuClose();
      setSourceMeasure(`[${t(e.target.innerText)}]`);
      setExpression(`[${t(e.target.innerText)}]`);
    } else {
      menuClose();
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_SelectOneMeasure") });
    }
  }

  const handleDateFormatter = (startDate, endDate) => {
    let start = startDate;
    let end = endDate;

    if (codeUtil.isWeekBucket(selectTD)) {
      start = `${startDate.substring(0, 4)}-${startDate.substring(5, 7)}`;
      end = `${endDate.substring(0, 4)}-${endDate.substring(5, 7)}`;
    } else if (selectTD === codeUtil.buckets.QUARTER) {
      const quarterStart = start.substring(5, 7);
      const quarterEnd = end.substring(5, 7);

      if (quarterStart === "1Q") {
        start = `${startDate.substring(0, 4)}-01`;
      } else if (quarterStart === "2Q") {
        start = `${startDate.substring(0, 4)}-04`;
      } else if (quarterStart === "3Q") {
        start = `${startDate.substring(0, 4)}-07`;
      } else {
        start = `${startDate.substring(0, 4)}-10`;
      }
      if (quarterEnd === "1Q") {
        end = `${endDate.substring(0, 4)}-03`;
      } else if (quarterEnd === "2Q") {
        end = `${endDate.substring(0, 4)}-06`;
      } else if (quarterEnd === "3Q") {
        end = `${endDate.substring(0, 4)}-09`;
      } else {
        end = `${endDate.substring(0, 4)}-12`;
      }
    } else if (selectTD === codeUtil.buckets.YEAR) {
      start = `${startDate.substring(0, 4)}-01`;
      end = `${endDate.substring(0, 4)}-12`;
    }
    setCopyToStartDate(new ZDate(start));
    setCopyToEndDate(new ZDate(end));
  };

  /*
   * Source 시작월을 고르면 Target 시작월도 **Source 가 움직인 버킷 수(gap)만큼** 같이 옮긴다.
   *   같은 달로 맞추는 게 아니라 둘 사이의 간격(오프셋)을 그대로 유지하는 것이다.
   *   예) Source 2026-03 → 2026-06 (+3), Target 2026-09 → 2026-12
   * Target 이 고를 수 있는 범위(첫 편집 가능 월 ~ 최대 월) 밖으로 나가면 그 범위 안으로 맞춘다.
   * 월/분기/연 버킷 전용이다 — 주 계열은 Target 시작이 헤더 날짜에 맞아야 해서 따로 계산한다.
   */
  const changeFromStartDate = (newStartValue) => {
    const prevFromStart = copyFromStartDate;
    setCopyFromStartDate(newStartValue);
    if (isWeekBucket || !newStartValue || !prevFromStart) {
      return;
    }
    const gap = differenceInMonths(monthInitDateFormat(newStartValue), monthInitDateFormat(prevFromStart));
    if (gap === 0) {
      return;
    }
    let shifted = addMonths(monthInitDateFormat(copyToStartDate), gap);
    const minStart = monthInitDateFormat(TO_START_DATE);
    const maxStart = monthInitDateFormat(END_MAX_DATE);
    if (shifted < minStart) {
      shifted = minStart;
    } else if (shifted > maxStart) {
      shifted = maxStart;
    }
    setCopyToStartDate(new ZDate(shifted));
  };

  const handleToggleClose = () => {
    setToggleOpen(false);
  };
  const handleToggle = () => {
    setToggleOpen(!toggleOpen);
  };

  /* 닫는 경로는 handleConfirm 하나다 — 예전 handleClose 는 Modal 의 onClose 에만 걸려 있었는데
     backdropClick/escapeKeyDown 을 둘 다 막아 놓아 실제로 불릴 일이 없는 죽은 코드였다. */
  const handleConfirm = () => {
    modalResultData({});
    modalResultData({ reload: true });
    setSourceMeasure("");
    setExpression("");
    setSelectedPeriod(periodList[0]);
    close(false);
  };

  const saveDemandVal = (dto) => {
    if (dto.versionDto?.statusCd === versionType.SCENARIO) {
      demandValService.upsertScenarioBulk({ ...dto })
        .then((response) => {
          if (response.data.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            if (response.data) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
              setTimeout(() => {
                //성공 후 2초후 자동으로 modal 닫힘
                handleConfirm();
              }, 2000);
            }
          }
        })
        .catch(response => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: response.response?.data?.message || t("MSG_SaveFailed") });
          debug(response);
          handleToggleClose();
        })
        .then(() => {
          handleToggleClose();
        });
    }
  };

  const monthInitDateFormat = (date) => {
    return new ZDate(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  }

  const changeCdExpression = (targetExp) => {
    let cdExpression = targetExp;
    if (enabledMeasure.length > 0) {
      for (const measure of enabledMeasure) {
        if (cdExpression.includes(`[${GridUtils.gridValueL10N(measure.name)}]`)) {
          cdExpression = cdExpression.replaceAll(`[${GridUtils.gridValueL10N(measure.name)}]`, `[${measure.measureCd}]`);
        } 
      }
    } 
    return cdExpression;
  }

  const setPivotData = (fromMeasureCd, toMeasureCd, isPage) => {
    const result = [];
    const removeData = [];
    for (const data of nodeData?.pivotData) {
      if (!isPage && data.key !== nodeData?.clickNodeData?.resultRow?.key) {
        continue;
      }
      if (data.measureCd !== fromMeasureCd && data.measureCd !== toMeasureCd) {
        continue;
      }
      //access control on 이면서 editableCustomerHrchy false이면 안됌(target이 아님)
      if (nodeData?.accessControl && !data.editableCustomerHrchy) {
        removeData.push(data.key);
        continue;
      }

      let idKey = "";
      if (data.customerId !== null) {
        idKey = data.customerId
      } else {
        idKey = data.customerHrchyId
      }
      if (data.itemId !== null) {
        idKey = `${idKey}${data.itemId}`
      } else {
        idKey = `${idKey}${data.itemHrchyId}`
      }
      const obj = {};
      obj.itemId = data.itemId;
      obj.itemCd = data.itemCd;
      obj.itemHrchyId = data.itemHrchyId;
      obj.itemHrchyCd = data.itemHrchyCd;
      obj.customerId = data.customerId;
      obj.customerCd = data.customerCd;
      obj.customerHrchyId = data.customerHrchyId;
      obj.customerHrchyCd = data.customerHrchyCd;
      obj.data = data.pivotData;
      obj.key = idKey;
      obj.measureCd = data.measureCd;
      obj.from = data.measureCd === fromMeasureCd
      if (fromMeasureCd === toMeasureCd) {
        const toMeasureObj = cloneDeep(obj);
        toMeasureObj.from = false;
        result.push(toMeasureObj)
      }
      result.push(obj);
    }

    const resultData = [];
    for (const remove of result) {
      if (removeData.includes(remove.key)) {
        continue;
      }
      resultData.push(remove);
    }
    return resultData; 
  }

  const bulkValueUpdate = () => {
    const workbenchDto = nodeData.clickNodeData.resultRow;
    if (!sourceMeasure) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoAvailableMeasure") });
      return;
    }
    if (!isPage) {
      handleToggle();
    } else {
      setSnackInfo({open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_TaskCompleteAlert")});
    }

    let sourceMeasureCd = changeCdExpression(sourceMeasure);
    sourceMeasureCd = sourceMeasureCd.slice(0, -1);
    sourceMeasureCd = sourceMeasureCd.slice(1, sourceMeasureCd.length);
    workbenchDto.measure.formulaExp = changeCdExpression(expression);
    workbenchDto.fromMeasure = nodeData.activeMeasure[sourceMeasureCd];
    workbenchDto.bulkFromStartDate = isWeekBucket ? copyFromStartDate : monthInitDateFormat(copyFromStartDate);
    workbenchDto.bulkFromEndDate = isWeekBucket ? copyFromEndDate : monthInitDateFormat(copyFromEndDate);
    workbenchDto.bulkToStartDate = isWeekBucket ? copyToStartDate : monthInitDateFormat(copyToStartDate);
    workbenchDto.bulkToEndDate = isWeekBucket ? copyToEndDate : monthInitDateFormat(copyToEndDate);
    workbenchDto.versionDto = selectVersion;
    workbenchDto.custConfigCd = nodeData.custLevel;
    workbenchDto.itemConfigCd = nodeData.itemLevel;
    workbenchDto.custHrchyNum = nodeData?.params?.custHrchyColumn ?? codeUtil.hrchyNum.HRCHY;
    workbenchDto.itemHrchyNum = nodeData?.params?.itemHrchyColumn ?? codeUtil.hrchyNum.HRCHY;
    /*
     * 조회에 쓴 Advanced Filter 를 저장에도 실어 보낸다.
     *   안 보내면 서버는 필터 없는 조회로 리프를 분해하므로, 화면에 없던 하위 조합까지 함께 갱신된다
     *   (일반 저장 saveDemandVal 이 advancedFilters 를 실어 보내는 것과 같은 이유).
     *   getRequestParams() 가 넣어 준 params.advancedFilters 는 이미 서버가 기대하는 맨 필터 트리다.
     */
    workbenchDto.advancedFilters = nodeData?.params?.advancedFilters ?? null;
    /* EOS 제외는 트리에서 빠져 params.excludeEos 로만 남는다. AF 가 있을 때만 서버가 쓰므로 AF 없을 땐 false 로 보낸다. */
    workbenchDto.excludeEos = workbenchDto.advancedFilters ? Boolean(nodeData?.params?.excludeEos) : false;

    workbenchDto.page = isPage;
    workbenchDto.version = isVersion;
    workbenchDto.pivotDatas = workbenchDto.version ? null : setPivotData(sourceMeasureCd, workbenchDto.measureCd, isPage);
    saveDemandVal(workbenchDto);
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      bulkValueUpdate();
    }
  };

  const calInBucket = (sDate, eDate) => {
    const format = "yyyy-MM-dd";
    if (nodeData?.reverseFormatHeaders == null || isEmpty(nodeData?.reverseFormatHeaders)) {
      return;
    }
    const headers = Object.values(nodeData?.reverseFormatHeaders);
    headers.sort();

    const startIndex = headers.indexOf(dateUtils.formatDate(sDate, format));
    const endDate = addDays(new ZDate(eDate), 1);
    let endIndex = startIndex;
    if (dateUtils.formatDate(endDate, format) > headers[headers.length - 1]) {
      endIndex = headers.length;
    } else {
      endIndex = headers.indexOf(dateUtils.formatDate(endDate, format))
    } 
    return endIndex - startIndex;
  }

  const handleSave = () => {
    const fromStart = isWeekBucket ? copyFromStartDate : monthInitDateFormat(copyFromStartDate);
    const fromEnd = isWeekBucket ? copyFromEndDate : monthInitDateFormat(copyFromEndDate);
    const toStart = isWeekBucket ? copyToStartDate : monthInitDateFormat(copyToStartDate);
    const toEnd = isWeekBucket ? copyToEndDate : monthInitDateFormat(copyToEndDate);

    if (selectTD === buckets.PARTIAL_WEEK) {
      const fromRange = calInBucket(fromStart, fromEnd);
      const toRange = calInBucket(toStart, toEnd);
      if (fromRange !== toRange) {
        setDialogInfo({ open: true, title: "error", content: t("MSG_ValidationTimeRange") })
        return;
      }
    } else {
      const fromGap = isWeekBucket ? differenceInCalendarDays(fromEnd, fromStart) : differenceInMonths(fromEnd, fromStart);
      const toGap = isWeekBucket ? differenceInCalendarDays(toEnd, toStart) : differenceInMonths(toEnd, toStart);
      if (fromGap !== toGap) {
        setDialogInfo({ open: true, title: "error", content: t("MSG_ValidationTimeRange") })
        return;
      }
      if (toStart > toEnd) {
        setDialogInfo({ open: true, title: "error", content: t("MSG_ValidationTimeRange") })
        return;
      }
    }
    setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
  };

  const handleChangeSelect = (event) => {
    if (event.target.name === "sourceMeasure") {
      setSourceMeasure(event.target.value);
      setExpression(`${event.target.value}`);
    } else if (event.target.name === "selectedPeriod") {
      setSelectedPeriod(event.target.value);
    } else {
      setExpression(event.target.value);
    }
  };

  const getCalDateBydifferenceInBucket = (computeEnd = true) => {
    if (nodeData?.reverseFormatHeaders == null || isEmpty(nodeData?.reverseFormatHeaders)) {
      return;
    }
    const headers = Object.values(nodeData.reverseFormatHeaders);
    headers.sort();
    
    const format = "yyyy-MM-dd";
    const range = calInBucket(copyFromStartDate, copyFromEndDate);
    if (computeEnd) {
      const toStartIndex =  headers.indexOf(dateUtils.formatDate(copyToStartDate, format));
      const toEndIndex = toStartIndex + range;
      if (toEndIndex >= headers.length) {
        const date = addDays(new ZDate(END_MAX_DATE), -1);
        return new ZDate(date);
      }
      let toEndDate = headers[toEndIndex];
      toEndDate = addDays(new ZDate(toEndDate), -1);
      return new ZDate(toEndDate);
    } else {
      //start date로 변경
      const startDate = dateUtils.startDateOfBucket(copyToEndDate, selectTD, startDay);
      const toEndIndex =  headers.indexOf(dateUtils.formatDate(startDate, format));
      const toStartIndex = toEndIndex - (range-1);
      if (toStartIndex < 0) {
        return new ZDate(FROM_START_DATE);
      }
      const toStartDate = headers[toStartIndex];
      return new ZDate(toStartDate);
    }
  }

  /*
   * 날짜 state 는 **값이 바뀔 때만** 갈아 끼운다.
   *   같은 날짜라도 매번 새 Date 객체를 넣으면 React 가 변경으로 보고 effect 를 다시 돌린다.
   *   아래 두 effect(Target 종료일 계산 ↔ 버킷 gap 보정)는 서로의 state 를 갱신하므로,
   *   값이 같은데도 객체만 바꾸면 gap 이 끝내 안 맞는 경우 서로를 무한히 깨우게 된다.
   */
  const isSameDate = (a, b) => !!a && !!b && new Date(a).getTime() === new Date(b).getTime();

  const calCopyToEndDate = () => {
    let calEndDate = null;
    if (isWeekBucket) {
      if (selectTD === buckets.PARTIAL_WEEK) {
        calEndDate = getCalDateBydifferenceInBucket(true);
      } else {
        const range = differenceInCalendarDays(copyFromEndDate, copyFromStartDate);
        calEndDate = addDays(new ZDate(copyToStartDate), range);
      }
    } else {
      const fromStart = monthInitDateFormat(copyFromStartDate);
      const fromEnd = monthInitDateFormat(copyFromEndDate);
      const range = differenceInMonths(fromEnd, fromStart);
      calEndDate = addMonths(new ZDate(copyToStartDate), range);
    }
    if (!calEndDate) {
      return;
    }
    const nextEndDate = calEndDate > END_MAX_DATE ? END_MAX_DATE : calEndDate;
    if (!isSameDate(nextEndDate, copyToEndDate)) {
      setCopyToEndDate(nextEndDate);
    }
  };

  const getBeforeDayWeekCd = (dayNum) => {
    const beforeDay = dayNum > 0 ? dayNum - 1 : 6;
    const weeks = Object.values(dayOfWeek)
    for (const week of weeks) {
      if (week.id === beforeDay) {
        return week.cd;
      }
    }
    return dayOfWeek.SUNDAY.cd;
  }

  useEffect(() => {
    calCopyToEndDate();
    if (copyFromEndDate < copyFromStartDate) {
      //bucket end date에 맞춰서 넣어줘야 함.
      const endDate = dateUtils.calculateRangeDate(copyFromStartDate, selectTD, startDay, 1, true)
      setCopyFromEndDate(endDate);
    }
    if (copyFromEndDate > END_MAX_DATE) {
      setCopyFromEndDate(END_MAX_DATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyToStartDate, copyFromEndDate, copyFromStartDate]);

  useEffect(() => {
    //check bucket gap
    const fromGap = calInBucket(copyFromStartDate, copyFromEndDate);
    const toGap = calInBucket(copyToStartDate, copyToEndDate);
    if (fromGap !== toGap) {
      const calStartDate = getCalDateBydifferenceInBucket(false);
      // 값이 같거나 계산하지 못했으면 건드리지 않는다 — Target 시작일을 갈아 끼우면 위 effect 가 다시 돈다.
      if (calStartDate && !isSameDate(calStartDate, copyToStartDate)) {
        setCopyToStartDate(calStartDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyToEndDate]);

  useEffect(() => {
    if (isEqual(selectedPeriod, periodList[1])) {
      setIsPeriod(true);
      if (isWeekBucket) {
        const startDayMonth = new ZDate(baseMonth.getFullYear(), baseMonth.getMonth()-1, 1, 0, 0, 0)
        const endDayMonth = dateUtils.endOfMonthDate(startDayMonth);
        if (selectTD === buckets.PARTIAL_WEEK) {
          setCopyFromStartDate(startDayMonth);
          setCopyFromEndDate(endDayMonth);
        } else {
          const nearStartDate = dateUtils.nearDay(startDayMonth, startDay, selectTD);
          const nearEndDate = dateUtils.nearDay(endDayMonth, getBeforeDayWeekCd(startDayId), selectTD);
          setCopyFromStartDate(nearStartDate)
          setCopyFromEndDate(nearEndDate)
        }
      } else {
        setCopyFromStartDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 0, 0, 0, 0));
        setCopyFromEndDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 0, 0, 0, 0));
      }
    } else if (isEqual(selectedPeriod, periodList[2])) {
      setIsPeriod(true);
      if (isWeekBucket) {
        const lastMonth = new ZDate(baseMonth.getFullYear(), baseMonth.getMonth()-1, 1, 0, 0, 0)
        const startDayMonth =new ZDate(lastMonth.getFullYear(), lastMonth.getMonth()-5, 1, 0, 0, 0);
        const endDayMonth = dateUtils.endOfMonthDate(lastMonth);
        if (selectTD === buckets.PARTIAL_WEEK) {
          setCopyFromStartDate(startDayMonth);
          setCopyFromEndDate(endDayMonth);
        } else {
          const nearStartDate = dateUtils.nearDay(startDayMonth, startDay, selectTD);
          const nearEndDate = dateUtils.nearDay(endDayMonth, getBeforeDayWeekCd(startDayId), selectTD);
          setCopyFromStartDate(nearStartDate)
          setCopyFromEndDate(nearEndDate)
        }
      } else {
        setCopyFromStartDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth() - 6, 5, 0, 0, 0));
        setCopyFromEndDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 0, 0, 0, 0));
      }
    } else if (isEqual(selectedPeriod, periodList[3])) {
      setIsPeriod(true);
      if (isWeekBucket) {
        const lastMonth = new ZDate(baseMonth.getFullYear(), baseMonth.getMonth()-1, 1, 0, 0, 0)
        const startDayMonth =new ZDate(lastMonth.getFullYear(), lastMonth.getMonth()-11, 1, 0, 0, 0);
        const endDayMonth = dateUtils.endOfMonthDate(lastMonth);
        if (selectTD === buckets.PARTIAL_WEEK) {
          setCopyFromStartDate(startDayMonth);
          setCopyFromEndDate(endDayMonth);
        } else {
          const nearStartDate = dateUtils.nearDay(startDayMonth, startDay, selectTD);
          const nearEndDate = dateUtils.nearDay(endDayMonth, getBeforeDayWeekCd(startDayId), selectTD);
          setCopyFromStartDate(nearStartDate)
          setCopyFromEndDate(nearEndDate)
        }
      } else {
        setCopyFromStartDate(new ZDate(baseMonth.getFullYear() - 1, baseMonth.getMonth(), 5, 0, 0, 0));
        setCopyFromEndDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 0, 0, 0, 0));
      }
    } else {
      setIsPeriod(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  useEffect(() => {
    if (expression.match(/\]\[|\]+[1-9]|[1-9]+\[|\[\[/) !== null) {
      setIsIncorrect(true);
    } else {
      setIsIncorrect(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  useEffect(() => {
    if (isVersion && isPage) {
      setIsPage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVersion])

  useEffect(() => {
    if (isVersion && isPage) {
      setIsVersion(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPage])
  //load
  useEffect(() => {
    if (open) {
      if (nodeData.startDate && nodeData.endDate) {
        handleDateFormatter(nodeData.editableHeaderSet[0],
          nodeData.editableHeaderSet.slice(-1)[0]);
      }
      if (nodeData.activeMeasure) {
        const unusedBulkUpsertMeasure = codeUtil.unusedBulkUpsertMeasure;
        const measures = [];
        for (const res in nodeData.activeMeasure) {
          if (Object.prototype.hasOwnProperty.call(nodeData.activeMeasure, res)) {
            if (unusedBulkUpsertMeasure.includes(res)) {
              continue;
            }
            if (nodeData.activeMeasure[res].computeFlg === false && nodeData.activeMeasure[res].valueTypeCd === nodeData.rowMeasure.valueTypeCd) {
              measures.push(nodeData.activeMeasure[res]);
            }
          }
        }
        measures.sort((a, b) => {
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          return 0;
        });
        setEnabledMeasure(measures);
      }
      if (nodeData.columnHeader) {
        if (isWeekBucket) {
          const start = new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), baseMonth.getDate(), 0, 0, 0);
          setCopyFromStartDate(start)
          const endDate = dateUtils.calculateRangeDate(start, selectTD, startDay, 1, true)
          setCopyFromEndDate(endDate)
          setCopyToStartDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), baseMonth.getDate(), 0, 0, 0));
          setCopyToEndDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), baseMonth.getDate(), 0, 0, 0));
        } else {
          setCopyFromStartDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 1, 0, 0, 0));
          setCopyToStartDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 1, 0, 0, 0));
          setCopyToEndDate(new ZDate(baseMonth.getFullYear(), baseMonth.getMonth(), 1, 0, 0, 0));
        }
      }
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    /* 배경 클릭·ESC 로는 닫지 않는다(원래 disableEscapeKeyDown + backdropClick 무시).
       닫기 버튼은 handleConfirm 이라 닫으면서 부모를 reload 한다 — cancel 이 아니라 close. */
    <BaseConfigModal
      open={open}
      onClose={(_, reason) => {
        if (toggleOpen) return; // 로딩 중엔 무시
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleConfirm();
      }}
      title={nodeData.title}
      cancelLabel={t("close")}
      onSave={handleSave}
      width={isWeekBucket ? 502 : 462}
    >
        {toggleOpen && (
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={toggleOpen}
          // onClick={handleToggleClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <Typography id="modal-modal-title" variant="p" component="p" >
          {t("customerHierarchy")} :  {nodeData.customerHrchy}
        </Typography>
        <Typography id="modal-modal-title" variant="p" component="p" >
          {t("itemHrchy")} :  {nodeData.itemHrchy}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "right" }} >
        </Box>
        <Divider sx={{ mt: 2, backgroundColor: grey[100], borderColor: grey[100], borderWidth: 1, mb: 2 }} />
        <LocalizationProvider dateAdapter={AdapterDateFns} >
          <Box size="small" style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "center", gap: 2 }} >
            <FormControl size="small" sx={{ width: "48%" }} required >
              <InputLabel>{t("period")}</InputLabel>
              <Select
                size="small"
                variant="outlined"
                name="selectedPeriod"
                label="period"
                value={selectedPeriod}
                defaultValue={periodList[0]}
                onChange={(event) => { handleChangeSelect(event) }}
              >
                {periodList.map((item) => {
                  return <MenuItem value={item} key={item}>{item}</MenuItem>
                })}
              </Select>
            </FormControl>
            <DatePicker
              label={isWeekBucket ? t("planStartDate") : t("planStartMonth")}
              views={isWeekBucket ? ['year', 'month', 'day'] : ['year', 'month']}
              format={isWeekBucket ? 'yyyy-MM-dd' : 'yyyy-MM'}
              shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, selectTD, startDayByVersion)}
              minDate={FROM_START_DATE}
              maxDate={END_MAX_DATE}
              value={copyFromStartDate}
              disabled={isPeriod}
              openTo={'month'}
              onAccept={changeFromStartDate}
              slotProps={{ textField: { size: "small", sx: { width: "48%" } } }}
            />
            <DatePicker
              label={isWeekBucket ? t("planEndDate") : t("planEndMonth")}
              views={isWeekBucket ? ['year', 'month', 'day'] : ['year', 'month']}
              format={isWeekBucket ? 'yyyy-MM-dd' : 'yyyy-MM'}
              shouldDisableDate={(e) => WorkbenchUtil.ableEndDayOfWeek(e, selectTD, startDayByVersion)}
              minDate={copyFromStartDate}
              maxDate={END_MAX_DATE}
              disabled={isPeriod}
              value={copyFromEndDate}
              openTo={'month'}
              onAccept={newEndValue => { setCopyFromEndDate(newEndValue); }}
              slotProps={{ textField: { size: "small", sx: { width: "48%" } } }}
            />
          </Box>
          <FormControl size="small" style={{ width: "100%" }} sx={{ mt: 2 }} required >
            <InputLabel>{t("sourceMeasure")}</InputLabel>
            <Select
              size="small"
              variant="outlined"
              name="sourceMeasure"
              label="Enabled Measure"
              value={sourceMeasure}
              defaultValue={""}
              onChange={(event) => { handleChangeSelect(event) }}
            >
              {enabledMeasure?.map((item) => {
                return <MenuItem value={`[${GridUtils.gridValueL10N(item.name)}]`} key={GridUtils.gridValueL10N(item.name)}>{GridUtils.gridValueL10N(item.name)}</MenuItem>
              })}
            </Select>
          </FormControl>
        </LocalizationProvider>
        <Box size="small" style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "center", mb: 1, mt: 1 }} >
          <ArrowDropDownIcon color={"info"} />
        </Box>
        <FormControl size="small" sx={{ textAlign: "left", width: "100%", mr: 1 }}>
          <InputLabel htmlFor="outlined-basic">{t("computeExpress")}</InputLabel>
          <OutlinedInput
            label={t("computeExpress")} id="outlined-basic" size="small" style={{ width: "100%" }}
            value={expression}
            error={isIncorrect}
            multiline
            required
            onChange={handleChangeSelect}
            onKeyPress={menuKeyPress}
            placeholder="ex) [measure]*1.1"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="toggle password visibility"
                  onClick={popoverClick}
                  edge="end"
                >
                  {<QuestionMark color="primary" sx={{ fontSize: "16px" }} />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        <Box size="small" style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "center", mt: 1 }} >
          <ArrowDropDownIcon color={"info"} />
        </Box>
        <LocalizationProvider dateAdapter={AdapterDateFns} >
          <Box size="small" style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "center", gap: 2 }} >
            <DatePicker
              label={isWeekBucket ? t("planStartDate") : t("planStartMonth")}
              views={isWeekBucket ? ['year', 'month', 'day'] : ['year', 'month']}
              format={isWeekBucket ? 'yyyy-MM-dd' : 'yyyy-MM'}
              shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, selectTD, startDayByVersion)}
              minDate={TO_START_DATE}
              maxDate={END_MAX_DATE}
              value={copyToStartDate}
              openTo={'month'}
              onAccept={newStartValue => { setCopyToStartDate(newStartValue); }}
              slotProps={{ textField: { size: "small", sx: { width: "48%" } } }}
            />
            <DatePicker
              label={isWeekBucket ? t("planEndDate") : t("planEndMonth")}
              views={isWeekBucket ? ['year', 'month', 'day'] : ['year', 'month']}
              format={isWeekBucket ? 'yyyy-MM-dd' : 'yyyy-MM'}
              minDate={TO_START_DATE}
              maxDate={END_MAX_DATE}
              value={copyToEndDate}
              openTo={'month'}
              disabled
              readOnly
              onAccept={newEndValue => { setCopyToEndDate(newEndValue); }}
              slotProps={{ textField: { size: "small", sx: { width: "48%" } } }}
            />
          </Box>
        </LocalizationProvider>
        <FormControl size="small" style={{ width: "100%" }} sx={{ mt: 2 }} required >
          <TextField size="small" disabled value={t(nodeData.measure)} sx={{ width: "100%" }} label={t("targetMeasure")} />
        </FormControl>
        <FormControl sx={{ height: "30px" }}>
          <FormControlLabel
            control={<Checkbox size="small" name="currentPage" checked={isPage} onChange={() => { setIsPage(!isPage) }} />}
            label={t("currentPage")}
          />
          {/*<FormControlLabel*/}
          {/*  control={<Checkbox size="small" name="currentVersionBulkUpsert" checked={isVersion} onChange={() => { setIsVersion(!isVersion) }} />}*/}
          {/*  label={t("currentVersionBulkUpsert")}*/}
          {/*/>*/}
        </FormControl>
        <Divider sx={{ mt: 2, backgroundColor: grey[100], borderColor: grey[100], borderWidth: 1, mb: 2 }} />
        {/* 바깥 Box 는 아래 Dialog/Snackbar/Popover/Menu 를 담을 뿐이다 — 전부 포털이라 여백이 필요 없다. */}
        <Box>
          <Dialog
            open={dialogInfo.open}
            onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
            title={dialogInfo.title}
            content={dialogInfo.content}
            onHandler={handleDialog}
          />
          <Snackbar
            open={snackInfo.open}
            onClose={() => setSnackInfo({ ...snackInfo, open: false })}
            severity={snackInfo.severity}
            content={snackInfo.content}
          />
          <Popover
            id={id}
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={popoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Typography sx={{ p: 1.2, fontSize: FS.H2 }}>
              {t("availableCals")}:<strong> + - * / </strong><br></br>
              {t("MSG_FormulaExplanation")} <br></br>
              ex&#41; &#91;MEASURE&#93;* 1.1</Typography>
          </Popover>
          <Menu
            id="basic-menu"
            MenuListProps={{
              'aria-labelledby': 'outlined-basic',
            }}
            anchorEl={menuEl}
            open={menuOpen}
            onClose={menuClose}
          >
            {enabledMeasure?.map((item) => {
              return <MenuItem onClick={addExpression} value={t(item.name)} key={item.name}>{t(item.name)}</MenuItem>
            })}
          </Menu>
        </Box>
    </BaseConfigModal>
  )
}

export default withTranslation()(BulkValueUpdateModal);